import { isAicopyVoucherOrderOption } from "./aicopyOrderOptions";

/** AI 跟单：USDC 币种 ID（结算 convertCurrencyId） */
export const AICOPY_USDC_CODE = "USDC";
export const AICOPY_USDC_CURRENCY_ID = 2730;

export function isAicopyUsdt(code) {
  return String(code || "").toUpperCase() === "USDT";
}

export function isAicopyUsdc(code) {
  return String(code || "").toUpperCase() === AICOPY_USDC_CODE;
}

/** 仅 USDT 直接支付；USDC / 法币需先兑换为 USDT */
export function needsAicopyConvertBeforePay(code) {
  const upper = String(code || "").toUpperCase();
  if (!upper || isAicopyUsdt(upper) || isAicopyVoucherOrderOption(upper)) return false;
  return true;
}

/** 结算到账：USDT 不传 convertCurrencyId；USDC / 法币传对应 currencyId */
export function getAicopySettlementConvertCurrencyId(code, fiatTargetId = null) {
  const upper = String(code || "").toUpperCase();
  if (upper === "USDT") return undefined;
  if (upper === AICOPY_USDC_CODE) return AICOPY_USDC_CURRENCY_ID;
  return fiatTargetId ?? null;
}

/** 下单币种下拉：USDT、USDC + 用户法币 */
export function getAicopyOrderCurrencyOptions(fiatOptions = []) {
  const fiats = (fiatOptions || [])
    .map((c) => String(c || "").toUpperCase())
    .filter((c) => c && c !== "USDT" && c !== AICOPY_USDC_CODE);
  return ["USDT", AICOPY_USDC_CODE, ...fiats];
}

/** 结算币种下拉：USDT、USDC + 可兑换法币 */
export function getAicopySettlementCurrencyOptions(fiatOptionCodes = [], convertConfigList = [], findUsdtToFiatConfig) {
  const fiats = (fiatOptionCodes || [])
    .map((c) => String(c || "").toUpperCase())
    .filter(
      (c) =>
        c &&
        !isAicopyUsdt(c) &&
        !isAicopyUsdc(c) &&
        typeof findUsdtToFiatConfig === "function" &&
        findUsdtToFiatConfig(convertConfigList, c)
    );
  return ["USDT", AICOPY_USDC_CODE, ...fiats];
}

/** 下单限额换算：法币取 wallet fiatRate 与 convert exchangeRate 的较大值（1 USDT = rate 法币） */
export function resolveAicopyPayConvertRate({ currency, fiatRate, convertConfig }) {
  if (isAicopyUsdc(currency)) {
    const r = parseFloat(convertConfig?.exchangeRate);
    return Number.isFinite(r) && r > 0 ? r : 1;
  }
  if (!isAicopyUsdt(currency) && !isAicopyVoucherOrderOption(currency)) {
    const rates = [fiatRate, convertConfig?.exchangeRate]
      .map((v) => parseFloat(v))
      .filter((r) => Number.isFinite(r) && r > 0);
    return rates.length ? Math.max(...rates) : 1;
  }
  return 1;
}

function readAicopyAssetAvailableRaw(result, currencyCode) {
  const upper = String(currencyCode || "").toUpperCase();
  if (!upper) return "0";
  if (result?.assets && Array.isArray(result.assets)) {
    const asset = result.assets.find((a) => String(a.currency || "").toUpperCase() === upper);
    if (asset != null) {
      return String(asset.availableAmount ?? asset.available ?? "0");
    }
  } else if (result && typeof result === "object" && result[upper]) {
    const a = result[upper];
    return String(a.availableAmount ?? a.available ?? "0");
  } else if (result?.totalUsdtAmount != null && upper === "USDT") {
    return String(result.totalUsdtAmount);
  }
  return "0";
}

/** API 余额字符串：保留全部有效小数，仅去掉尾部无意义 0 */
export function normalizeAicopyBalanceRaw(raw) {
  let s = String(raw ?? "").replace(/,/g, "").trim();
  if (!s || !/^-?\d+(\.\d+)?$/.test(s)) return "0";
  if (!s.includes(".")) return s;
  s = s.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return s || "0";
}

/** 从 balances 接口读取指定币种可用余额（原始字符串，全精度） */
export function parseAicopyAssetBalanceRaw(result, currencyCode) {
  return normalizeAicopyBalanceRaw(readAicopyAssetAvailableRaw(result, currencyCode));
}

export function parseAicopyBalanceNum(balanceStr) {
  const n = parseFloat(String(balanceStr ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

/** Available 展示：全精度 + 可选千分位 */
export function formatAicopyAvailableBalanceDisplay(raw, formatNumber) {
  const normalized = normalizeAicopyBalanceRaw(raw);
  return formatNumber ? formatNumber(normalized) : normalized;
}

/** 滑块上限：余额与策略 hardCap 取较小者（字符串，全精度） */
export function resolveAicopySliderMaxBalanceStr(balanceStr, hardCapNum) {
  const balStr = normalizeAicopyBalanceRaw(balanceStr);
  const balNum = parseAicopyBalanceNum(balStr);
  if (!Number.isFinite(hardCapNum) || hardCapNum <= 0 || balNum <= hardCapNum + 1e-12) {
    return balStr;
  }
  return normalizeAicopyBalanceRaw(String(hardCapNum));
}

/** 滑块中间档位：按 max 的小数位数计算比例金额 */
export function resolveSliderAmountDecimals(maxStr, minPoint = 2) {
  const s = String(maxStr ?? "").replace(/,/g, "").trim();
  const dot = s.indexOf(".");
  if (dot === -1) return minPoint;
  return Math.max(minPoint, s.length - dot - 1);
}

/** 当前选中支付币种的可用余额字符串（全精度，无千分位） */
export function resolveAicopyCurrentAvailableBalanceStr({
  currency,
  availableBalance,
  usdcAvailableBalance,
  fiatBalanceMap,
  isVoucherPay = false,
}) {
  if (isVoucherPay || isAicopyUsdt(currency)) return normalizeAicopyBalanceRaw(availableBalance);
  if (isAicopyUsdc(currency)) return normalizeAicopyBalanceRaw(usdcAvailableBalance);
  const code = String(currency || "").toUpperCase();
  return normalizeAicopyBalanceRaw(fiatBalanceMap?.[code] ?? "0");
}

export function parseAicopyAssetBalance(result, currencyCode) {
  return parseAicopyBalanceNum(parseAicopyAssetBalanceRaw(result, currencyCode));
}
